import { ChangeDetectionStrategy, Component, ElementRef, inject, input, output, viewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { MatListModule, MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { connectState } from '@examdojo/angular/util';
import { IconComponent } from '@examdojo/core/icon';
import { fromResize } from '@examdojo/core/rxjs';
import { ContentItemFormGroup, ContentItemViewerComponent } from '@examdojo/question-content-item';
import { isNotNullish } from '@examdojo/util/nullish';
import { distinctUntilChanged, filter, map, startWith, switchMap } from 'rxjs';
import { OptionsItemFormGroup } from '../question-item-editor.model';
import { OptionLabelComponent } from './option-label.component';

@Component({
  selector: 'dojo-question-item-options-content-viewer',
  imports: [ContentItemViewerComponent, ReactiveFormsModule, IconComponent, OptionLabelComponent, MatListModule],
  template: `
    <dojo-content-item-viewer
      [contentItemsFormArray]="optionsFormGroup().controls.content_items"
      [isAllowedToEditImage]="isAllowedToEditImage()"
      [isAllowedToEditTable]="isAllowedToEditTable()"
      (editItem)="editOptionSubItem($event)"
    />
    <mat-selection-list
      class="options-container layout-one-column needs-horizontal-scroll"
      [class.force-one-column]="state.shouldForceOneColumn"
      [multiple]="false"
      (selectionChange)="onSelectionChange($event)"
    >
      @for (option of optionsFormGroup().controls.options.controls; track $index) {
        <mat-list-option
          [value]="$index"
          [disabled]="disabledOptionIndexes()?.includes($index)"
          [selected]="selectedOptionIndexes() && selectedOptionIndexes()!.includes($index)"
          class="option-element"
          [class.correct]="correctOptionIndexes() && correctOptionIndexes()!.includes($index)"
          [class.incorrect]="incorrectOptionIndexes() && incorrectOptionIndexes()!.includes($index)"
          [class.no-selectable]="!optionsSelectable()"
        >
          <div class="wrap-content flex w-full items-center gap-2">
            <dojo-option-label
              [index]="$index"
              class="option-label inline-flex h-[30px] min-w-[30px] items-center justify-center rounded-[0.5rem] border p-1 text-sm font-medium"
            />
            <dojo-content-item-viewer
              class="content-item-viewer !w-auto flex-1"
              [contentItemsFormArray]="option.controls.content_items"
              [isAllowedToEditImage]="isAllowedToEditImage()"
              [isAllowedToEditTable]="isAllowedToEditTable()"
              (editItem)="editOptionSubItem($event, $index)"
            />
            <div class="z-1 relative mr-4 flex h-[30px] w-[30px] items-center justify-center">
              @if (correctOptionIndexes() && correctOptionIndexes()!.includes($index)) {
                <dojo-icon [size]="20" [iconClass]="'text-green-600'" [icon]="'check'" />
              } @else if (incorrectOptionIndexes() && incorrectOptionIndexes()!.includes($index)) {
                <dojo-icon [size]="20" [iconClass]="'text-red-600'" [icon]="'xmark'" />
              }
            </div>
          </div>
        </mat-list-option>
      }
    </mat-selection-list>
    @if (showWorkedSolution() && optionsFormGroup().controls.markscheme.controls.worked_solution) {
      <div
        class="worked-solution-container mt-5 shrink-0 overflow-x-auto overflow-y-hidden border-t border-neutral-200 pt-4 md:mb-4 md:mt-8 md:pt-7"
      >
        <h4 class="text-md text-primary mb-3 font-semibold">Worked Solution</h4>
        <dojo-content-item-viewer
          [contentItemsFormArray]="optionsFormGroup().controls.markscheme.controls.worked_solution!"
          [isAllowedToEditImage]="isAllowedToEditImage()"
          [isAllowedToEditTable]="isAllowedToEditTable()"
          (editItem)="handleEditWorkedSolutionItem($event)"
        />
      </div>
    }
  `,
  host: {
    class: 'flex w-full flex-col min-w-0',
  },
  styleUrls: ['./question-item-options-content-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class QuestionItemOptionsContentViewerComponent {
  readonly optionsFormGroup = input.required<OptionsItemFormGroup>();
  readonly isAllowedToEditImage = input<boolean>(false);
  readonly isAllowedToEditTable = input<boolean>(false);
  readonly correctOptionIndexes = input<number[] | null>(null);
  readonly incorrectOptionIndexes = input<number[] | null>(null);
  readonly disabledOptionIndexes = input<number[] | null>(null);
  readonly selectedOptionIndexes = input<number[] | null>(null);
  readonly showWorkedSolution = input<boolean>(false);

  readonly optionsSelectable = input<boolean>(false);

  readonly optionSelected = output<number>();
  readonly editItem = output<{ item: ContentItemFormGroup; contentItemIndex: number }>();
  readonly editOptionItem = output<{ item: ContentItemFormGroup; contentItemIndex: number; optionIndex: number }>();
  readonly editWorkedSolutionItem = output<{ item: ContentItemFormGroup; contentItemIndex: number }>();

  private readonly hostElementRef = inject(ElementRef<HTMLElement>);

  private readonly selectionListRef = viewChild<MatSelectionList, ElementRef<HTMLElement>>(MatSelectionList, {
    read: ElementRef,
  });

  private readonly shouldForceOneColumn$ = toObservable(this.selectionListRef).pipe(
    filter(isNotNullish),
    switchMap((elementRef) =>
      fromResize(elementRef.nativeElement, { debounceTime: 200 }).pipe(
        startWith(null),
        map(() => this.checkOverflowWithClone(elementRef.nativeElement)),
      ),
    ),
    distinctUntilChanged(),
  );

  readonly state = connectState({
    shouldForceOneColumn: this.shouldForceOneColumn$,
  });

  editOptionSubItem(
    { item, contentItemIndex }: { item: ContentItemFormGroup; contentItemIndex: number },
    optionIndex?: number,
  ) {
    if (optionIndex !== undefined) {
      this.editOptionItem.emit({
        item,
        contentItemIndex,
        optionIndex,
      });
    } else {
      this.editItem.emit({
        item,
        contentItemIndex,
      });
    }
  }

  handleEditWorkedSolutionItem({ item, contentItemIndex }: { item: ContentItemFormGroup; contentItemIndex: number }) {
    this.editWorkedSolutionItem.emit({
      item,
      contentItemIndex,
    });
  }

  onSelectionChange(event: MatSelectionListChange) {
    const selectedOptions = event.source.selectedOptions.selected.map((option) => option.value);

    if (selectedOptions.length > 0) {
      this.optionSelected.emit(selectedOptions[0]);
    }
  }

  private checkOverflowWithClone(listNative: HTMLElement): boolean {
    // Prevent flickering layout by checking overflow on a hidden clone
    const listClone = listNative.cloneNode(true) as HTMLElement;
    listClone.classList.remove('force-one-column');
    listClone.style.position = 'absolute';
    listClone.style.top = '-9999px';
    listClone.style.visibility = 'hidden';
    listClone.style.pointerEvents = 'none';

    this.hostElementRef.nativeElement.appendChild(listClone);

    const cloneWrapContents = listClone.querySelectorAll('.wrap-content');
    const hasOverflow = Array.from(cloneWrapContents).some(
      (wrap) => (wrap as HTMLElement).scrollWidth > (wrap as HTMLElement).clientWidth,
    );

    this.hostElementRef.nativeElement.removeChild(listClone);

    return hasOverflow;
  }
}
